import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { BrandingService, KundeTokenService } from '@comparit/infrastructure';
import { NavigatorComponent, PageNavigatorService } from '@comparit/shared';
import {
  CustomIconsService,
  ExtRouteService,
  HeaderComponent,
  customDateAdapter,
} from '@comparit/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { KfzTaskSyncService } from '../services/kfz-task-sync.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CalculatorStateService } from '../services/calculator-state.service';
import { VermittlerCacheService } from '../services/vermittler-cache.service';
import { FunnelLoginService } from '../services/funnel-login.service';
import { KundeTaskStartService } from '../services/kunde-task-start.service';

@Component({
  imports: [
    CommonModule,
    NgComponentOutlet,
    HeaderComponent,
    MatSidenavModule,
    RouterOutlet,
    NavigatorComponent,
    MatProgressSpinnerModule,
  ],
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  // FunnelLoginService is scoped here on purpose — see its class doc: only the
  // funnel may reach form state through it.
  providers: [customDateAdapter, FunnelLoginService, KundeTaskStartService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent implements OnInit {
  private readonly brandingService = inject(BrandingService);
  private readonly _customIconsService = inject(CustomIconsService);
  private readonly _extRouteService = inject(ExtRouteService);
  private readonly taskSyncService = inject(KfzTaskSyncService);
  private readonly calcState = inject(CalculatorStateService);
  private readonly router = inject(Router);
  protected readonly vermittlerCache = inject(VermittlerCacheService);
  protected readonly funnelLogin = inject(FunnelLoginService);
  private readonly navigatorService = inject(PageNavigatorService);
  private readonly kundeToken = inject(KundeTokenService);
  private readonly kundeTaskStart = inject(KundeTaskStartService);

  private footerHost = viewChild<ElementRef<HTMLElement>>('footerHost');

  protected navigatorBottomPx = signal(0);

  constructor() {
    this._customIconsService.init();

    effect(onCleanup => {
      const el = this.footerHost()?.nativeElement;
      if (!el) {
        this.navigatorBottomPx.set(0);
        return;
      }

      let scheduled = false;
      const update = () => {
        scheduled = false;
        const rect = el.getBoundingClientRect();
        const overlap = Math.min(rect.height, Math.max(0, window.innerHeight - rect.top));
        this.navigatorBottomPx.set(Math.round(overlap));
      };
      const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(update);
      };

      update();
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      const observer = new ResizeObserver(schedule);
      observer.observe(el);
      observer.observe(document.body);

      onCleanup(() => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        observer.disconnect();
      });
    });
  }

  ngOnInit(): void {
    this.calcState.init();

    // Started here rather than an app initializer: /laden lives outside the
    // shell precisely so FormsService is not constructed before storage
    // hydration.
    this.taskSyncService.init();
    this.kundeTaskStart.init();
  }

  protected brandConfig = this.brandingService.config;
  protected showNavigator = computed(() => !this._extRouteService.url().endsWith('step1'));
  protected stepInfo = computed(() => this.navigatorService.setup()?.info);

  protected navigateToStart(): void {
    const portalUrl = this.brandConfig()?.portalUrl;
    if (portalUrl && this.kundeToken.isLoggedIn() && !this.kundeToken.windowMode()) {
      window.open(portalUrl, '_blank');
      return;
    }
    this.router.navigate(['/formular']);
  }
}
